import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useMemo, useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {updateProcedureRun} from 'procedures/network/ProcedureRunDAO';
import buildFullExperimentPlan from 'procedures/planning/buildFullExperimentPlan';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {TimingEstimateResult} from 'studies/progress/useTimingEstimate';
import {
  getProcedureRunUIStringForStudyType,
  getProcedureTemplateStringForStudyType,
} from 'studies/TypedStudyUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_LIGHT, NEAR_BLACK} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {withLoadingScreen} from 'util/Promises';

import {StudyInaccessibleContentBanner} from '../utils/ui/StudyQueryBanners';
import {
  MultiProcedureRunStudyDesignTabContentsQuery,
  MultiProcedureRunStudyDesignTabContentsQueryVariables,
  MultiProcedureRunStudyDesignTabContentsQuery_procedureRun_ProcedureRunNode_templateProcedureFlowchartVersion_ProcedureFlowchartVersionNode as AccessibleTemplateProcedureFlowchartVersionNode,
  MultiProcedureRunStudyDesignTabContentsQuery_procedureRun_ProcedureRunNode as ProcedureRunNode,
} from './__generated/MultiProcedureRunStudyDesignTabContentsQuery';
import getStudyDesignFromProcedureRun, {
  STUDY_DESIGN_FROM_PROCEDURE_RUN_FRAGMENT,
} from './getStudyDesignFromProcedureRun';
import {
  areProcedureRunInputsEvaluatedValuesValid,
  isNumConditionReplicatesValid,
  validateConditions,
} from './procedureRunStudyValidationUtils';
import {serializeExperimentConditionsForNetwork} from './ProcessDevelopmentStudyNetworkTranslators';
import {ProcessDevelopmentStudyPlanEditor} from './ProcessDevelopmentStudyPlanEditor';
import StudyDesignTabSavingButtons from './StudyDesignTabSavingButtons';
import {useMethodExecutionLimitState} from './useMethodExecutionLimitState';
import {
  useConditionsAndExperimentalInputs,
  useExperimentalInputsContextProperties,
} from './useProcessDevelopmentStudyState';

const NO_ESTIMATE: TimingEstimateResult = {stale: false, executionPlan: null};

export const MULTI_PROCEDURE_RUN_STUDY_DESIGN_TAB_CONTENTS_QUERY: DocumentNode = gql`
  query MultiProcedureRunStudyDesignTabContentsQuery($procedureRunId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    procedureRun(id: $procedureRunId) {
      ...GetStudyDesignFromProcedureRunGraphQL
      ... on ProcedureRunNode {
        id
        procedureType
        defaultDataEntryMode
        flowchartIsExpanded
        conditions {
          id
          name
          description
          procedureRunConditionReplicates {
            id
          }
        }
        templateProcedureFlowchartVersion {
          ...ExperimentalInputsFlowchartGraphQL
          ... on ProcedureFlowchartVersionNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedureFlowchart {
              id
              parent {
                id
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcedureFlowchartVersion').fragment}
  ${STUDY_DESIGN_FROM_PROCEDURE_RUN_FRAGMENT}
`;

function MultiProcedureRunStudyDesignTabContents(props: {
  procedureRun: ProcedureRunNode & {
    templateProcedureFlowchartVersion: AccessibleTemplateProcedureFlowchartVersionNode;
  };
  studyType: StudyType;
}): JSX.Element {
  const {procedureRun} = props;
  const {experimentalInputs: initialExperimentalInputs, templateFlowchart} = useMemo(
    () => getStudyDesignFromProcedureRun(procedureRun),
    [procedureRun]
  );
  const [selectedMethodId, setSelectedMethodId] = useState(templateFlowchart.nodes[0]?.id ?? null);
  const [isEditing, setIsEditing] = useState(false);
  const initialConditions = useMemo(
    () =>
      procedureRun.conditions.map((condition) => ({
        id: condition.id,
        numReplicates: condition.procedureRunConditionReplicates.length,
        name: condition.name,
        description: condition.description ?? undefined,
      })),
    [procedureRun]
  );
  assert(
    procedureRun.templateProcedureFlowchartVersion.procedureFlowchart.parent != null,
    'Template flowchart version should always have a non-null parent.'
  );
  const procedureId = procedureRun.templateProcedureFlowchartVersion.procedureFlowchart.parent.id;

  const {conditions, setConditions, experimentalInputs, setExperimentalInputs} =
    useConditionsAndExperimentalInputs(
      {
        id: procedureId,
        procedureType: procedureRun.procedureType,
        methods: procedureRun.templateProcedureFlowchartVersion.nodes,
      },
      procedureId,
      initialConditions,
      initialExperimentalInputs
    );
  // useConditionsAndExperimentalInputs returns null conditions when the procedure ID is null, which should
  // never be the case here
  assert(conditions != null, 'Conditions should not be null for existing run');

  const context = useExperimentalInputsContextProperties(
    conditions,
    experimentalInputs,
    setExperimentalInputs,
    procedureRun.templateProcedureFlowchartVersion,
    procedureRun.procedureType,
    // Should be locked when not in editing state
    !isEditing
  );
  // Ideally `context` would have a conditional return type based on experimentalInputs null-ness.
  assert(context != null, 'Expected context to be non-null');

  const methodExecutionLimitState = useMethodExecutionLimitState(
    experimentalInputs,
    conditions,
    procedureRun.templateProcedureFlowchartVersion.largestTableTypeSizes,
    procedureRun.defaultDataEntryMode
  );

  async function handleSave(): Promise<void> {
    setIsEditing(false);
    assert(conditions != null, 'Conditions should not be null for existing run');
    assert(experimentalInputs != null, 'Experimental inputs should not be null for existing run.');
    assert(context != null, 'Expected context to be non-null');

    // For simplicity, we serialize the full list of conditions and then take only the new conditions at the
    // end rather than modifying every input to the experiment plan to only include the new conditions
    const replicatesPerCondition = conditions.map((condition) => condition.numReplicates);
    const experimentPlan = buildFullExperimentPlan({
      replicatesPerCondition,
      namesPerCondition: conditions.map((condition) => condition.name),
      descriptionsPerCondition: conditions.map((condition) => condition.description),
      numConditions: replicatesPerCondition.length,
      experimentalInputs,
      evaluatedValues: context.evaluatedValues,
    });
    const serializedConditions = serializeExperimentConditionsForNetwork(experimentPlan);
    // TODO (BNCH-124614): This only works if we can assume that the persisted condition columns
    // cannot be reordered. If we can't assume that, then we need to know the indexes of the
    // the persisted conditions.
    const serializedNewConditions = serializedConditions.slice(initialConditions.length);
    await withLoadingScreen(
      updateProcedureRun({id: procedureRun.id, newConditions: serializedNewConditions}),
      'Creating new conditions...'
    );
  }

  return selectedMethodId == null ? (
    // Unexpected since we should prevent creating a procedure run if there are no methods, but handle it gracefully.
    <div className={css(styles.bufferTabWrapper)}>
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text={`No ${getProcedureUIString(ProceduresStringKeys.METHOD, procedureRun.procedureType, {
          stringCase: StringCase.LOWER,
          plural: true,
        })} have been configured for this ${getProcedureUIString(
          ProceduresStringKeys.PROCEDURE,
          procedureRun.procedureType,
          {
            stringCase: StringCase.LOWER,
          }
        )}.`}
      />
    </div>
  ) : (
    <div className={css(styles.designBackground)}>
      {BenchlingConfig.STUDIES_ENABLE_ADD_CONDITIONS && !procedureRun.flowchartIsExpanded && (
        <div className={css(styles.header)}>
          <div className={css(styles.tabTitle)}>Design</div>
          <div className={css(styles.bannerAndButtons)}>
            {isEditing && (
              <InlineBanner
                bannerType={InlineBannerType.INFO}
                text="New worksheets will be created for new conditions."
              />
            )}
            <StudyDesignTabSavingButtons
              onCancel={() => {
                setIsEditing(false);
                setConditions(
                  initialConditions,
                  // TODO (BNCH-124614): This only works if we can assume that the persisted condition columns
                  // cannot be reordered. If we can't assume that, then we need to know the indexes of the
                  // the persisted conditions.
                  Array.from({length: initialConditions.length}, (_, i) => i)
                );
              }}
              onEdit={() => {
                setIsEditing(true);
              }}
              // TODO (BNCH-124345): Show disabled save button with spinner while we're waiting for the new
              // conditions to be created.
              onSave={handleSave}
              isEditing={isEditing}
              isSaveDisabled={
                !areProcedureRunInputsEvaluatedValuesValid(context.evaluatedValues) ||
                !validateConditions(conditions) ||
                !isNumConditionReplicatesValid(methodExecutionLimitState) ||
                conditions.length === initialConditions.length
              }
            />
          </div>
        </div>
      )}
      <ProcessDevelopmentStudyPlanEditor
        conditions={conditions}
        setConditions={setConditions}
        experimentalInputs={experimentalInputs}
        setExperimentalInputs={setExperimentalInputs}
        context={context}
        procedureFlowchart={templateFlowchart}
        procedureMethods={templateFlowchart.nodes}
        selectedMethodId={selectedMethodId}
        setSelectedMethodId={setSelectedMethodId}
        methodExecutionLimitState={methodExecutionLimitState}
        // TODO (BNCH-124338): Provide estimate
        estimate={NO_ESTIMATE}
        procedureType={procedureRun.procedureType}
        experimentName={procedureRun.name}
        // Updating experiment name for existing runs is not currently supported
        setExperimentName={noop}
        // Set to false because at this stage because the experiment name has been created and in the design tab
        // we don't allow the user to set the experiment name. This is only relevant when first filling out the
        // experiment name in the add experiment modal.
        hasExperimentNameTemplate={false}
        showExperimentName={false}
        dataTestAttrs={{component: 'MultiProcedureRunStudyDesignTabContents', element: 'root'}}
      />
    </div>
  );
}

export default function QueryingMultiProcedureRunStudyDesignTabContents({
  procedureRunId,
  studyType,
}: {
  procedureRunId: string;
  studyType: StudyType;
}): JSX.Element {
  const result = useBenchlingQuery<
    MultiProcedureRunStudyDesignTabContentsQuery,
    MultiProcedureRunStudyDesignTabContentsQueryVariables
  >(MULTI_PROCEDURE_RUN_STUDY_DESIGN_TAB_CONTENTS_QUERY, {variables: {procedureRunId}});
  if (result.type !== QueryResultType.SUCCESS) {
    return <CenteredHorizontalThrobber />;
  } else {
    const {data} = result;
    if (isInaccessible(data.procedureRun)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.procedureRun}
          modelType={ModelTypeConstants.PROCEDURE_RUN}
          displayErrorText={`This ${getProcedureRunUIStringForStudyType(studyType)} is no longer accessible. The ${getProcedureRunUIStringForStudyType(studyType)} may have been moved into a project that you do not have access to.`}
        />
      );
    } else if (isInaccessible(data.procedureRun.templateProcedureFlowchartVersion)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.procedureRun.templateProcedureFlowchartVersion}
          modelType={ModelTypeConstants.PROCEDURE}
          displayErrorText={`The design of this ${getProcedureRunUIStringForStudyType(studyType)} is not accessible. The ${getProcedureTemplateStringForStudyType(studyType)} associated with this ${getProcedureRunUIStringForStudyType(studyType)} may have been moved into a template collection that you do not have access to.`}
        />
      );
    } else {
      return (
        <MultiProcedureRunStudyDesignTabContents
          procedureRun={{
            ...data.procedureRun,
            templateProcedureFlowchartVersion: data.procedureRun.templateProcedureFlowchartVersion,
          }}
          studyType={studyType}
        />
      );
    }
  }
}

const styles = StyleSheet.create({
  bufferTabWrapper: {
    paddingTop: 10,
    paddingBottom: 10,
  },
  designBackground: {
    background: Color.WHITE,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 33px 8px 12px',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  bannerAndButtons: {
    display: 'flex',
    alignItems: 'center',
  },
  tabTitle: {
    color: NEAR_BLACK,
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '16px',
    display: 'flex',
    alignItems: 'center',
  },
});
